import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { optimizeAvatarFile } from "@/lib/media/optimize-image";
import { createId, getErrorMessage, safeStorageExtension } from "@/lib/utils";

export const PROFILE_AVATAR_BUCKET = "profile-avatars";
export const MAX_PROFILE_AVATAR_SOURCE_BYTES = 12 * 1024 * 1024;
export const MAX_PROFILE_AVATAR_BYTES = 1024 * 1024;
export const PROFILE_AVATAR_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export function profileAvatarUrl(path: string | null | undefined) {
  const baseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.replace(/\/$/, "");
  if (!path || !baseUrl) {
    return null;
  }

  return `${baseUrl}/storage/v1/object/public/${PROFILE_AVATAR_BUCKET}/${path}`;
}

export function validateProfileAvatarFile(file: File) {
  if (!PROFILE_AVATAR_TYPES.includes(file.type as (typeof PROFILE_AVATAR_TYPES)[number])) {
    return "Csak JPG, PNG vagy WebP kép tölthető fel.";
  }
  if (file.size > MAX_PROFILE_AVATAR_SOURCE_BYTES) {
    return "A kép legfeljebb 12 MB lehet.";
  }
  return null;
}

export async function uploadProfileAvatar(userId: string, file: File) {
  const sourceError = validateProfileAvatarFile(file);
  if (sourceError) {
    throw new Error(sourceError);
  }

  const optimized = await optimizeAvatarFile(file);
  if (optimized.size > MAX_PROFILE_AVATAR_BYTES) {
    throw new Error("A profilkép optimalizálás után is túl nagy. Próbáljon kisebb képet.");
  }

  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(optimized.name, "jpg");
  const path = `${userId}/avatar-${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(PROFILE_AVATAR_BUCKET)
    .upload(path, optimized, {
      cacheControl: "86400",
      contentType: optimized.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return path;
}

export async function removeProfileAvatar(path: string | null) {
  if (!path) {
    return;
  }

  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage
    .from(PROFILE_AVATAR_BUCKET)
    .remove([path]);

  if (error) {
    throw new Error(
      `A korábbi profilkép törlése nem sikerült: ${getErrorMessage(error)}`,
    );
  }
}
